import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Typography } from '@mui/material';
import { ColumnFlexBox } from '../../../Components/FlexBox';
import { useSetAtom, useAtomValue, useAtom } from 'jotai';
import useAPI from '../../../api';
import PageWrapper from '../../../Components/Paper/PageWrapper';
import {
  CHANNEL_TYPES,
  DEFAULT_STATS_TABS,
} from '../../../Utils/constants/general';
import { CALL_STANDARD_FILTERS } from '../../../Helpers/Filters/constants';
import CustomerDialog, {
  DEFAULT_PAGES,
} from '../../../Components/Dialog/EntityInfoDialog';
import ConfirmPopup from '../../../Components/Popup/ConfirmPopup';
import {
  openSnackbarAdviseAtom,
  selectedBranchAtom,
  reloadAtom,
  userBranchesAtom,
  searchValuesAtom,
} from '../../../Atoms';
import { createNewDate, culture } from '../../../Helpers/dates';
import capitalize from '../../../Helpers/StringsFormatting/capitalize';
import getDefaultColumns from '../../../Components/ContactListWrapper/utils/getDefaultColumns';
import LongChannelStatusChip from './LongChannelStatusChip';
import StatusInfo from '../../../Components/ContactListWrapper/EntryComponent/StatusInfo';
import StatusChip from '../../../Components/Chip/StatusChip';
import SegmentComponent from './SegmentComponent';
import {
  LONGCHANNEL_SORTING_OPTIONS,
  LONGCHANNEL_SEARCHBOX_FILTERS,
  LONGCHANNEL_FILTERS,
  DYNAMIC_FEATURES_IDS,
} from '../Utils/constants';
import LongChannelFilters from './Filters';
import LongChannelFeaturesPage from './LongChannelFeaturesPage';
import CaptionValue from '../../../Components/CaptionValue/CaptionValue';

const renderLongChannelStatusChip = ({ data, updateItem }) => (
  <LongChannelStatusChip data={data} updateItem={updateItem} />
);
const AllCustomersPage = function AllCustomersPage(props) {
  const intl = useIntl();
  const API = useAPI();

  const setOpenSnackbarAdvise = useSetAtom(openSnackbarAdviseAtom);
  const setReload = useSetAtom(reloadAtom);
  const userBranches = useAtomValue(userBranchesAtom);
  const selectedBranch = useAtomValue(selectedBranchAtom) || {};
  const [customerDialog, setCustomerDialog] = useState(false);
  const [confirmClosing, setConfirmClosing] = useState(null);
  const [searchValues, setSearchValues] = useAtom(searchValuesAtom);
  const [dealerRatingOptions, setDealerRatingOptions] = useState([]);

  const handleChangeSearchValues = (valueObj) => {
    setSearchValues((old) => ({
      ...old,
      ...valueObj,
    }));
  };

  const handleOpenDialog = () => {
    setCustomerDialog(true);
  };
  const handleCloseDialog = () => {
    setCustomerDialog(false);
  };

  const getDealerRatingOptions = () => {
    API.getDynamicFeaturesOptionsById(
      54, //dealer rating id
      { silent: true },
      (res) => {
        setDealerRatingOptions(res);
      },
      () => {}
    );
  };

  useEffect(() => {
    getDealerRatingOptions();
  }, []);

  const handleSubmit = (data) => {
    let payload = { ...data };
    if (selectedBranch?.channelType === CHANNEL_TYPES.Long) {
      const { features, ...restPayload } = data;
      const featuresArray = Object.keys(features || {}).map((key) => ({
        featureId: DYNAMIC_FEATURES_IDS[key], //dynamic feature DB Id
        featureValue:
          key === 'hasNationalAgreement'
            ? features[key] === intl.messages.YES
            : features[key],
      }));
      payload = {
        ...restPayload,
        dynamicFeatures: featuresArray,
      };
    }
    if (
      selectedBranch?.channelType === CHANNEL_TYPES.Short &&
      !payload?.companyCode
    ) {
      payload = { ...payload, companyCode: payload?.fiscalCode };
    }
    API.addCustomer(
      {
        ...payload,
        branchId: selectedBranch.id,
        income: payload?.income !== '' ? payload?.income : null,
        ...(payload?.mobilePhoneNumber
          ? {
              mobilePhoneNumber:
                payload?.mobilePhoneNumber?.substring(0, 3) === '+39'
                  ? payload?.mobilePhoneNumber
                  : `+39${payload?.mobilePhoneNumber}`,
            }
          : {}),
      },
      () => {
        handleCloseDialog();
        setReload(true);
        setOpenSnackbarAdvise({
          message: intl.messages.NEW_CUSTOMER_CREATE_SUCCESSFULLY,
          severity: 'success',
        });
      },
      (err) => {
        setOpenSnackbarAdvise({
          message: `${intl.messages.ERROR}: ${
            err?.status ? `${err?.status} - ` : ''
          } ${intl.messages.CREATE_CUSTOMER_ERROR}`,
          severity: 'error',
        });
      }
    );
  };

  const columns = [
    ...getDefaultColumns(userBranches, selectedBranch),
    {
      id: 'active',
      noCaption: true,
      formatCallback: (value) =>
        value ? (
          <div />
        ) : (
          <StatusInfo
            value={value ? undefined : 'INACTIVE'}
            optionsColors={{ INACTIVE: 'error.light' }}
          />
        ),
    },
  ];

  const longChannelColumns = [
    {
      id: 'companyName',
      size: { md: 2 },
      noCaption: true,
      formatCallback: (value, data) => (
        <ColumnFlexBox>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {capitalize(value)}
          </Typography>
          <CaptionValue
            direction="row"
            gap="4px"
            caption="RATING"
            valueStyle={{ fontWeight: 'bold', color: 'primary.main' }}
            value={
              dealerRatingOptions?.find(
                (option) => option?.value === data?.dealerRating
              )?.displayValue || '-'
            }
          />
        </ColumnFlexBox>
      ),
    },
    {
      id: 'customerCode',
      size: { md: 1 },
    },
    {
      id: 'fullName',
      label: intl.messages.FINANCIALMANAGER,
      size: { md: 1.2 },
      formatCallback: (value) => capitalize(value),
    },
    {
      id: 'mobilePhoneNumber',
      size: { md: 1.2 },
      formatCallback: (value) =>
        userBranches.length > 1 && selectedBranch && value ? (
          <a
            style={{
              color: 'inherit',
              fontFamily: 'Roboto',
              fontWeight: 600,
              fontSize: '0.95rem',
              letterSpacing: '0.02em',
              textDecoration: 'none',
            }}
            href={`tel:${selectedBranch.prefix3CX}${value.replace('+39', '')}`}
          >
            {value}
          </a>
        ) : (
          value
        ),
    },
    {
      id: 'category',
      noCaption: true,
      size: { md: 1.5 },
      formatCallback: (value) => (
        <SegmentComponent label="SEGMENT" value={value} />
      ),
    },
    {
      id: 'hasNationalAgreement',
      label: intl.messages.NATIONALAGREEMENT,
      formatCallback: (value) => (value ? intl.messages.YES : intl.messages.NO),
    },
    {
      id: 'status',
      noCaption: true,
      size: { md: 1 },
      formatCallback: (value) =>
        value ? (
          <StatusInfo
            value={value}
            optionsColors={{
              AT: 'success.light',
              PE: 'warning.light',
              CH: 'error.light',
              SC: 'info.light',
            }}
          />
        ) : (
          <div />
        ),
    },
    {
      id: 'exportedOn',
      noCaption: true,
      size: { md: 1.2 },
      formatCallback: (value) => (
        <StatusChip
          alternativeLabel={capitalize(
            `${createNewDate(value).toLocaleDateString(culture, {
              month: 'long',
              year: 'numeric',
            })}`
          )}
          status={'exportedOn'}
          options={{ exportedOn: null }}
          colors={{ exportedOn: '#c6cbf0' }}
        />
      ),
    },
  ];

  return (
    <>
      <PageWrapper
        {...props}
        tablet={false}
        standardFilters={
          selectedBranch?.channelType === CHANNEL_TYPES.Long
            ? LONGCHANNEL_FILTERS
            : CALL_STANDARD_FILTERS.filter(
                ({ keyName }) => keyName !== 'subsectionId'
              )
        }
        customFilters={
          selectedBranch?.channelType === CHANNEL_TYPES.Long ? (
            <LongChannelFilters
              defaultValues={searchValues}
              onConfirm={handleChangeSearchValues}
              branchId={selectedBranch?.id}
              dealerRatingOptions={dealerRatingOptions}
            />
          ) : null
        }
        statsTabs={DEFAULT_STATS_TABS}
        apiType="All"
        period={0}
        fab={intl.messages.NEW_CUSTOMER}
        fabVariant="extended"
        onClickFab={handleOpenDialog}
        columns={
          selectedBranch?.channelType === CHANNEL_TYPES.Long
            ? longChannelColumns
            : columns
        }
        customStatusChipCallback={
          selectedBranch?.channelType === CHANNEL_TYPES.Long
            ? renderLongChannelStatusChip
            : undefined
        }
        searchBoxFilters={
          selectedBranch?.channelType === CHANNEL_TYPES.Long
            ? LONGCHANNEL_SEARCHBOX_FILTERS
            : undefined
        }
        sortingOptions={
          selectedBranch?.channelType === CHANNEL_TYPES.Long
            ? LONGCHANNEL_SORTING_OPTIONS
            : undefined //uses the default one
        }
      />
      <CustomerDialog
        title={intl.messages.NEW_CUSTOMER}
        onSubmit={handleSubmit}
        open={Boolean(customerDialog)}
        data={customerDialog}
        isLongChannel={selectedBranch?.channelType === CHANNEL_TYPES.Long}
        pages={
          selectedBranch?.channelType === CHANNEL_TYPES.Long
            ? [...DEFAULT_PAGES, LongChannelFeaturesPage]
            : DEFAULT_PAGES
        }
        close={(e, isFormDirty) => {
          if (isFormDirty) setConfirmClosing(e.currentTarget);
          else {
            handleCloseDialog();
          }
        }}
        branchId={selectedBranch.id}
      />
      <ConfirmPopup
        confirmText={intl.messages.CLOSE_WINDOW}
        open={Boolean(confirmClosing)}
        anchorEl={confirmClosing}
        close={() => setConfirmClosing(null)}
        handleConfirm={handleCloseDialog}
      />
    </>
  );
};

export default AllCustomersPage;
